import { Flex } from "@chakra-ui/layout";
import { CampaignTopic, SignalSelectionSource } from "@intentsify/types";
import { isDefined } from "@intentsify/utils";
import { TableVirtualised } from "components";
import difference from "lodash/difference";
import differenceWith from "lodash/differenceWith";
import uniqBy from "lodash/uniqBy";
import { useMemo } from "react";
import { useKeywordsAndTopicsState } from "screens/Campaigns/screens/CampaignsWizard/components/KeywordsAndTopicsStep/KeywordsAndTopics.hooks";
import { KeywordItem, SelectionMode } from "types/Keyword";
import { useColumns } from "./ResultsTable.columns";
import { ResultsTableEntry, useData } from "./ResultsTable.data";
import {
  ResultsTableTab,
  ResultsTableTabs,
  useTabs,
} from "./ResultsTable.tabs";
import { useRowSelection } from "./useRowSelection/useRowSelection";
import { useSyncSelectionFromState } from "./useRowSelection/useSyncSelectionFromState";

interface ResultsTableProps {
  search?: string;
}

export const ResultsTable = (props: ResultsTableProps) => {
  const { state, actions } = useKeywordsAndTopicsState();
  const { categoryIndex, selectedTab, setCategoryIndex } = useTabs();
  const data = useData({ tab: selectedTab, search: props.search });
  const columns = useColumns();
  const [keywordRowSelection, setKeywordRowSelection] = useRowSelection();
  const [topicRowSelection, setTopicRowSelection] = useRowSelection();
  const [productRowSelection, setProductRowSelection] = useRowSelection();

  const rowSelection = useMemo(() => {
    if (selectedTab === ResultsTableTab.Keywords) {
      return keywordRowSelection;
    }

    if (selectedTab === ResultsTableTab.Topics) {
      return topicRowSelection;
    }

    if (selectedTab === ResultsTableTab.Products) {
      return productRowSelection;
    }

    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    throw new Error(`Unknown tab: ${selectedTab}`);
  }, [
    keywordRowSelection,
    productRowSelection,
    selectedTab,
    topicRowSelection,
  ]);

  const setRowSelection = useMemo(() => {
    if (selectedTab === ResultsTableTab.Keywords) {
      return setKeywordRowSelection;
    }

    if (selectedTab === ResultsTableTab.Topics) {
      return setTopicRowSelection;
    }

    if (selectedTab === ResultsTableTab.Products) {
      return setProductRowSelection;
    }

    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    throw new Error(`Unknown tab: ${selectedTab}`);
  }, [
    selectedTab,
    setKeywordRowSelection,
    setProductRowSelection,
    setTopicRowSelection,
  ]);

  useSyncSelectionFromState({
    tab: selectedTab,
    onSync: setRowSelection,
    search: props.search,
  });

  return (
    <Flex width="100%">
      <ResultsTableTabs
        w="200px"
        variant="corporate"
        orientation="vertical"
        categoryIndex={categoryIndex}
        setCategoryIndex={setCategoryIndex}
      />

      <TableVirtualised<ResultsTableEntry>
        selectable="checkbox-multi-select"
        clearSelectionOnSort={false}
        data={data}
        columns={columns}
        rowSelection={rowSelection}
        setRowSelection={(rowSelectionStateUpdater) => {
          const nextRowSelectionState =
            typeof rowSelectionStateUpdater === "function"
              ? rowSelectionStateUpdater(rowSelection)
              : rowSelectionStateUpdater;

          const searchLower = props.search?.toLowerCase();

          if (selectedTab === ResultsTableTab.Keywords) {
            const selectedState = Object.keys(nextRowSelectionState).map(
              (rowIndex) => {
                const entry =
                  state.autodiscoverySettings.autodiscoveryKeywords.filter(
                    (k) =>
                      searchLower
                        ? k.keyword.toLowerCase().includes(searchLower)
                        : true
                  )[rowIndex as unknown as number];

                return entry;
              }
            );

            const alreadySelectedKeywords = state.selectedKeywords.filter(
              (k) => k.metaData?.source === SignalSelectionSource.Autodiscovery
            );

            let nextKeywords: KeywordItem[] = [];
            if (searchLower) {
              nextKeywords = [
                ...state.selectedKeywords.filter(
                  (k) =>
                    k.metaData?.source ===
                      SignalSelectionSource.Autodiscovery &&
                    !k.label.toLowerCase().includes(searchLower)
                ),
                ...selectedState.map((s) => ({
                  value: s.keywordId,
                  label: s.keyword,
                  metaData: { source: SignalSelectionSource.Autodiscovery },
                })),
              ] as KeywordItem[];
            } else {
              nextKeywords = selectedState.map((s) => ({
                value: s.keywordId,
                label: s.keyword,
                metaData: { source: SignalSelectionSource.Autodiscovery },
              })) as KeywordItem[];
            }

            if (alreadySelectedKeywords.length > nextKeywords.length) {
              actions.setSelectedKeywords(
                differenceWith(
                  alreadySelectedKeywords,
                  nextKeywords,
                  (a, b) => a.label.toLowerCase() === b.label.toLowerCase(),
                ),
                SelectionMode.UNSELECT
              );
            }

            if (alreadySelectedKeywords.length < nextKeywords.length) {
              actions.setSelectedKeywords(
                uniqBy(nextKeywords, (k) => k.label),
                SelectionMode.SELECT
              );
            }
          }

          if (selectedTab === ResultsTableTab.Topics) {
            const shouldDeselect =
              Object.keys(rowSelection).length >
              Object.keys(nextRowSelectionState).length;

            const indicesToDeselect = difference(
              Object.keys(rowSelection),
              Object.keys(nextRowSelectionState)
            ).map((i) => Number(i));

            const selectedState = Object.keys(nextRowSelectionState)
              .map((rowIndex) => {
                const entry =
                  state.autodiscoverySettings.autodiscoveryTopics.filter((t) =>
                    searchLower
                      ? t.topic.toLowerCase().includes(searchLower)
                      : true
                  )[Number(rowIndex)];

                if (
                  indicesToDeselect.includes(Number(rowIndex)) &&
                  shouldDeselect
                ) {
                  return;
                }

                return entry;
              })
              .filter(isDefined);

            const autodiscoveryTopicIds = new Set(
              state.autodiscoverySettings.autodiscoveryTopics.map(
                (topic) => topic.topicId,
              ),
            );
            const alreadySelectedTopics = state.selectedTopics.filter(
              (topic) =>
                topic.source === SignalSelectionSource.Autodiscovery &&
                autodiscoveryTopicIds.has(topic.topicId),
            );

            let nextTopics: CampaignTopic[] = [];

            if (searchLower) {
              nextTopics = [
                ...state.selectedTopics.filter(
                  (t) =>
                    t.source === SignalSelectionSource.Autodiscovery &&
                    !t.topic.toLowerCase().includes(searchLower)
                ),
                ...selectedState.map((s) => ({
                  source: SignalSelectionSource.Autodiscovery,
                  topic: s.topic,
                  topicId: s.topicId,
                })),
              ] as CampaignTopic[];
            } else {
              nextTopics = selectedState.map((s) => ({
                source: SignalSelectionSource.Autodiscovery,
                topic: s.topic,
                topicId: s.topicId,
              })) as CampaignTopic[];
            }

            if (shouldDeselect) {
              actions.setSelectedTopics(
                differenceWith(
                  alreadySelectedTopics,
                  nextTopics,
                  (a, b) => a.topicId === b.topicId
                ),
                SelectionMode.UNSELECT
              );
            } else {
              actions.setSelectedTopics(
                uniqBy(
                  [...nextTopics, ...alreadySelectedTopics],
                  (k) => k.topic
                ),
                SelectionMode.SELECT
              );
            }
          }

          if (selectedTab === ResultsTableTab.Products) {
            const shouldDeselect =
              Object.keys(rowSelection).length >
              Object.keys(nextRowSelectionState).length;

            const indicesToDeselect = difference(
              Object.keys(rowSelection),
              Object.keys(nextRowSelectionState)
            ).map((i) => Number(i));

            const selectedState = Object.keys(nextRowSelectionState)
              .map((rowIndex) => {
                const entry =
                  state.autodiscoverySettings.autodiscoveryProducts.filter(
                    (t) =>
                      searchLower
                        ? t.topic.toLowerCase().includes(searchLower)
                        : true
                  )[Number(rowIndex)];

                if (
                  indicesToDeselect.includes(Number(rowIndex)) &&
                  shouldDeselect
                ) {
                  return;
                }

                return entry;
              })
              .filter(isDefined);

            const autodiscoveryProductIds = new Set(
              state.autodiscoverySettings.autodiscoveryProducts.map(
                (product) => product.topicId,
              ),
            );
            const alreadySelectedTopics = state.selectedTopics.filter(
              (topic) =>
                topic.source === SignalSelectionSource.Autodiscovery &&
                autodiscoveryProductIds.has(topic.topicId),
            );

            let nextTopics: CampaignTopic[] = [];

            if (searchLower) {
              nextTopics = [
                ...state.selectedTopics.filter(
                  (t) =>
                    t.source === SignalSelectionSource.Autodiscovery &&
                    !t.topic.toLowerCase().includes(searchLower)
                ),
                ...selectedState.map((s) => ({
                  source: SignalSelectionSource.Autodiscovery,
                  topic: s.topic,
                  topicId: s.topicId,
                })),
              ] as CampaignTopic[];
            } else {
              nextTopics = selectedState.map((s) => ({
                source: SignalSelectionSource.Autodiscovery,
                topic: s.topic,
                topicId: s.topicId,
              })) as CampaignTopic[];
            }

            if (shouldDeselect) {
              actions.setSelectedTopics(
                differenceWith(
                  alreadySelectedTopics,
                  nextTopics,
                  (a, b) => a.topicId === b.topicId
                ),
                SelectionMode.UNSELECT
              );
            } else {
              actions.setSelectedTopics(
                uniqBy(
                  [...nextTopics, ...alreadySelectedTopics],
                  (k) => k.topic
                ),
                SelectionMode.SELECT
              );
            }
          }

          return setRowSelection(nextRowSelectionState);
        }}
        containerProps={{ width: "100%", height: "500px" }}
      />
    </Flex>
  );
};
